/**
 * Page-level coordinator for hover-less hover-previews — used on touch
 * devices where mouseenter doesn't fire.
 *
 * Each LiveTile subscribes on mount and reports its viewport intersection
 * via IntersectionObserver. The coordinator picks the single tile closest
 * to the top of the viewport that meets the activation threshold and tells
 * it to play; everything else gets paused. As the user scrolls, "the active
 * tile" passes from one to the next — same pattern as TikTok / IG Reels.
 *
 * "Keep going" semantics: when no tile currently meets the activation
 * threshold but the previously-active tile is *still partially visible*,
 * it keeps playing. Only when the active tile fully leaves the viewport
 * (ratio drops to 0) does it pause and clear. This avoids twitchy
 * pause-then-resume during mid-scroll transitions, without burning cycles
 * playing videos that are entirely off-screen.
 *
 * Pauses when the tab becomes hidden (Page Visibility API). Resumes the
 * previously-active tile when the tab becomes visible again.
 *
 * Lives at module scope: a single coordinator handles every LiveTile
 * rendered on the page regardless of which route mounted them.
 */

export interface Subscriber {
  setActive: (active: boolean) => void;
}

interface VisibilityReport {
  ratio: number;
  /** Distance from the viewport top to the element's top edge, in pixels.
   *  Used to sort visible subscribers and pick the topmost. */
  top: number;
}

// Threshold a tile must reach to *take over* as the active one. Low because
// a higher value lets the previous tile hang on far past the point the user
// has clearly moved on — by the time the new tile is 50% visible, the old
// one is often barely on-screen at all and still "active." 10% catches the
// new tile as soon as it meaningfully enters from below.
const ACTIVATION_THRESHOLD = 0.1;

const subscribers = new Set<Subscriber>();
const visible = new Map<Subscriber, VisibilityReport>();
let activeSub: Subscriber | null = null;

// SSR guard — module loads at import time but DOM globals may not exist.
const HAS_DOM = typeof document !== "undefined";
let tabVisible = HAS_DOM ? !document.hidden : true;

function setActiveExclusive(next: Subscriber | null) {
  if (next === activeSub) return;
  const prev = activeSub;
  activeSub = next;
  // Pause prev first so we never have two playing simultaneously while
  // re-entering React's render queue.
  if (prev) prev.setActive(false);
  if (next) next.setActive(true);
}

function recompute() {
  if (!tabVisible) return;

  // Eligible tiles for taking over the active slot: meets the visibility
  // threshold AND its top edge is still in the viewport (top >= 0). Once a
  // tile's top scrolls past the viewport top (top < 0), it stops qualifying
  // for activation — the user has clearly moved past it. The previous active
  // can stay playing via "keep going" while it's still partially visible.
  //
  // Among the eligible, pick the topmost — smallest top — so when several
  // are entering at once (multi-column grids) the leftmost-of-uppermost wins.
  const eligible: Array<[Subscriber, VisibilityReport]> = [];
  for (const [sub, v] of visible) {
    if (v.ratio >= ACTIVATION_THRESHOLD && v.top >= 0) {
      eligible.push([sub, v]);
    }
  }
  eligible.sort((a, b) => a[1].top - b[1].top);

  const candidate = eligible[0]?.[0] ?? null;

  if (candidate) {
    setActiveExclusive(candidate);
    return;
  }

  // No tile clears the activation bar. "Keep going" — if the previously
  // active tile is still in the viewport at all (ratio > 0, which means
  // it's in `visible`), leave it playing. Otherwise pause and clear.
  if (activeSub && !visible.has(activeSub)) {
    setActiveExclusive(null);
  }
}

/**
 * Add a tile to the coordinator. Returns an unsubscribe fn that the caller
 * must invoke on unmount so the registry doesn't leak across route changes.
 */
export function register(sub: Subscriber): () => void {
  subscribers.add(sub);
  return () => {
    subscribers.delete(sub);
    visible.delete(sub);
    if (activeSub === sub) {
      activeSub = null;
      // A different visible tile may now be eligible — let recompute pick.
      recompute();
    }
  };
}

/**
 * Push a fresh visibility reading for a subscriber. Call from the
 * IntersectionObserver callback. `ratio` of 0 fully removes the entry
 * (the tile is entirely off-screen).
 */
export function report(sub: Subscriber, ratio: number, top: number): void {
  if (ratio <= 0) {
    visible.delete(sub);
  } else {
    visible.set(sub, { ratio, top });
  }
  recompute();
}

// Pause when the tab hides; resume the same tile when it returns. We
// don't clear activeSub on hide so resume is exact, not "whichever is
// topmost now" — which could differ if the layout changed.
if (HAS_DOM) {
  document.addEventListener("visibilitychange", () => {
    tabVisible = !document.hidden;
    if (!activeSub) return;
    activeSub.setActive(tabVisible);
  });
}
